import PurchaseOrderTable from './PurchaseOrderTable'
import Tab from '@mui/material/Tab';
import TabPanel from '../../TabPanel';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tabs, Typography } from '@mui/material';
import { useState, useEffect } from 'react';
import styles from './index.module.css'
import AddIcon from '@mui/icons-material/Add';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import { fetchAllPurchaseOrders, fetchArchivedPurchaseOrders, fetchArchivedPurchaseOrdersCount, restoreArchivedPurchaseOrder, invalidateCache } from '../../../services/purchaseOrder'
import { getUsersByRole } from '../../../services/users'
import { fetchOperationsManagementSettings } from '../../../services/operationsManagement';
import { useParams } from 'react-router-dom';
import RegeneratePOsPopup from './RegeneratePOsPopup'
import ProofsList from "./ProofsList";
import { buildPoProofsPagePath } from "./PurchaseOrderTable/PurchaseOrderNumberCell";

const PurchaseOrderList = ({ order, setRegeneratePopup, regeneratePopup, setSelectedPOList, fetchOrder, selectedProducts, handleShowProductsPopup, requestProofs, refreshKey = 0 }) => {
    const { instanceUniqueId, orderId, tabId, tabtype } = useParams();
    const [productionRepOption, setProductionRepOption] = useState([])
    const [currentTab, setCurrentTab] = useState(0);
    const [purchaseOrders, setPurchaseOrders] = useState(null)
    const [loading, setLoading] = useState(false);
    const [archiveCount, setArchiveCount] = useState(0);
    const [archiveOpen, setArchiveOpen] = useState(false);
    const [archiveRows, setArchiveRows] = useState([]);
    const [archiveLoading, setArchiveLoading] = useState(false);
    const [archiveError, setArchiveError] = useState("");
    const [restoringPoId, setRestoringPoId] = useState(null);
    const [showPoUrl, setShowPoUrl] = useState(false);
    const [showPoNotesAction, setShowPoNotesAction] = useState(true);


    const handleChange = () => {
        setCurrentTab(currentTab === 0 ? 1 : 0)
    }

    const handleCloseRegeneratePopup = () => {
        setRegeneratePopup(false);
    };
    const fetchPurchaseOrders = async () => {
        try {
            setLoading(true)
            const response = await fetchAllPurchaseOrders(tabId)
            console.log(tabId, response)
            setPurchaseOrders(response)
            setLoading(false)
            // A count query is inexpensive. Only hydrate archived POs on demand.
            try {
                setArchiveCount(await fetchArchivedPurchaseOrdersCount(tabId, instanceUniqueId));
            } catch (archiveCountError) {
                console.warn('Could not fetch archived PO count', archiveCountError);
                setArchiveCount(0);
            }
        } catch (error) {
            setLoading(false)
            console.log(error)
        }
    }

    const openPoArchive = async () => {
        setArchiveOpen(true);
        setArchiveLoading(true);
        setArchiveError("");
        setArchiveRows([]);
        try {
            setArchiveRows(await fetchArchivedPurchaseOrders(tabId, instanceUniqueId));
        } catch (error) {
            console.error('Could not load archived POs', error);
            setArchiveError('Archived purchase orders could not be loaded.');
        } finally {
            setArchiveLoading(false);
        }
    };

    const restorePo = async (po) => {
        if (!window.confirm(`Restore PO #${po.poNumber || po.id} to active purchasing?`)) return;
        setRestoringPoId(po.id);
        setArchiveError("");
        try {
            await restoreArchivedPurchaseOrder(tabId, po.id, instanceUniqueId);
            setArchiveRows((rows) => rows.filter((row) => row.id !== po.id));
            await fetchPurchaseOrders();
        } catch (error) {
            setArchiveError(error?.response?.data?.message || 'Unable to restore this purchase order.');
        } finally {
            setRestoringPoId(null);
        }
    };

    const fetchProductionRep = async () => {
        try {
            const response = await getUsersByRole('purchasing-rep')
            console.log('setProductionRepOption', response)
            setProductionRepOption(response?.users || [])
        } catch (error) {
            console.log(error)
        }
    }

    const loadPurchasingSettings = async () => {
        if (!instanceUniqueId) {
            setShowPoUrl(false);
            setShowPoNotesAction(true);
            return;
        }

        try {
            const response = await fetchOperationsManagementSettings(instanceUniqueId);
            setShowPoUrl(response?.settings?.purchasing?.showPoUrl === true);
            setShowPoNotesAction(
                response?.settings?.purchasing?.showPoNotesAction !== false
            );
        } catch (error) {
            console.error('Failed to load Purchasing settings for PO page', error);
            setShowPoUrl(false);
            setShowPoNotesAction(true);
        }
    }

    useEffect(() => {
        fetchPurchaseOrders()
        fetchProductionRep()
        loadPurchasingSettings()
    }, [instanceUniqueId, tabId, refreshKey])

    const pathname = window.location.pathname;
    const shouldRenderLegacyPoTabs = pathname.includes('/pos-proofs/');
    const poProofsPageUrl = buildPoProofsPagePath({
        instanceUniqueId,
        orderId,
        documentId: tabId,
        pathname,
    });

    return (
        shouldRenderLegacyPoTabs && (
            <Box className={styles.presentationTabContainer} data-po-proofs-page="true">
                {/* No orientation prop here */}
                <RegeneratePOsPopup purchaseOrders={purchaseOrders} open={regeneratePopup}
                    onClose={handleCloseRegeneratePopup} fetchPurchaseOrders={fetchPurchaseOrders}/>

                <Tabs
                    value={currentTab}
                    onChange={handleChange}
                    aria-label="Horizontal tabs example"
                    sx={{
                        marginBottom: "10px !important", // Ensures no margin below the Tabs
                        paddingBottom: 0, // Ensures no padding below the Tabs
                        padding: 0,
                        "& .MuiTabs-indicator": { backgroundColor: "#c3171d" }, // This will hide the default indicator under selected tab
                    }}
                >
                    <Tab
                        sx={{
                            color:
                                currentTab === 0 ? "#c3171d !important" : "#000001 !important",
                            backgroundColor:
                                currentTab === 0 ? "white !important" : "transparent !important",
                        }}
                        label="Purchase Orders"
                        className={styles.tabLabel}
                    />
                    <Tab
                        sx={{
                            color:
                                currentTab === 1 ? "#c3171d !important" : "#000001 !important",
                            backgroundColor:
                                currentTab === 1 ? "white !important" : "transparent !important",
                        }}
                        label="Proofs"
                        className={styles.tabLabel}
                    />
                </Tabs>

                <TabPanel value={currentTab} index={0} >
                    <Box sx={{
                        padding: 0,
                        margin: 0,
                    }}
                    >
                        <Paper elevation={3} sx={{ padding: 0 }}>
                            {archiveCount > 0 && (
                                <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ px: 1.5, py: 1 }}>
                                    <Button
                                        startIcon={<ArchiveOutlinedIcon />}
                                        size="small"
                                        color="inherit"
                                        variant="outlined"
                                        onClick={openPoArchive}
                                        aria-label={`View archived purchase orders (${archiveCount})`}
                                    >
                                        Archived POs
                                    </Button>
                                    <Chip label={archiveCount} size="small" variant="outlined" aria-label={`${archiveCount} archived purchase orders`} />
                                </Stack>
                            )}
                            <PurchaseOrderTable setSelectedPOList={setSelectedPOList} requestProofs={requestProofs} fetchPurchaseOrders={fetchPurchaseOrders} productionRepOption={productionRepOption} purchaseOrders={purchaseOrders} loading={loading} setLoading={setLoading} showPoUrl={showPoUrl} showPoNotesAction={showPoNotesAction} poProofsPageUrl={poProofsPageUrl} />
                        </Paper>
                    </Box>
                </TabPanel >

                <Dialog open={archiveOpen} onClose={() => setArchiveOpen(false)} fullWidth maxWidth="md">
                    <DialogTitle>Archived Purchase Orders</DialogTitle>
                    <DialogContent>
                        <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
                            Archived POs are excluded from active purchasing. Authorized purchasing users can restore them.
                        </Typography>
                        {archiveError && <Typography color="error" role="alert">{archiveError}</Typography>}
                        {archiveLoading ? (
                            <Typography role="status">Loading archived purchase orders…</Typography>
                        ) : archiveRows.length === 0 && !archiveError ? (
                            <Typography>No archived purchase orders for this sales order.</Typography>
                        ) : (
                            <Table size="small">
                                <TableHead><TableRow>
                                    <TableCell>PO #</TableCell><TableCell>Supplier / Decorator</TableCell>
                                    <TableCell>Ship to</TableCell><TableCell>Status</TableCell><TableCell>Document</TableCell><TableCell>Action</TableCell>
                                </TableRow></TableHead>
                                <TableBody>
                                    {archiveRows.map((po) => {
                                        const info = typeof po.supplierInfo === 'string'
                                            ? (() => { try { return JSON.parse(po.supplierInfo); } catch (_) { return {}; } })()
                                            : (po.supplierInfo || {});
                                        const pdfs = Array.isArray(po.pdfs) ? po.pdfs : [];
                                        const pdf = pdfs.find((item) => item?.active) || pdfs[pdfs.length - 1];
                                        return <TableRow key={po.id}>
                                            <TableCell>{po.poNumber || po.id}</TableCell>
                                            <TableCell>{info.supplierName || '—'}</TableCell>
                                            <TableCell>{info.shipTo?.name || info.shipTo?.city || '—'}</TableCell>
                                            <TableCell><Chip label="Archived" color="default" size="small" variant="outlined" /></TableCell>
                                            <TableCell>{pdf?.url
                                                ? <Button size="small" href={pdf.url} component="a" target="_blank" rel="noopener noreferrer">View PDF</Button>
                                                : 'No PDF'}</TableCell>
                                             <TableCell><Button size="small" variant="outlined" disabled={restoringPoId !== null} onClick={() => restorePo(po)}>{restoringPoId === po.id ? 'Restoring…' : 'Restore PO'}</Button></TableCell>
                                        </TableRow>;
                                    })}
                                </TableBody>
                            </Table>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setArchiveOpen(false)}>Close</Button>
                    </DialogActions>
                </Dialog>

                <TabPanel value={currentTab} index={1}>
                    <ProofsList />
                </TabPanel>
            </Box>
        )
    );
};
export default PurchaseOrderList;
